import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { getPricing } from "../firebase/pricingService";
import { useCart } from "../context/CartContext";
import { FRAME_TYPES, SIZES } from "../firebase/constants";

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@300;400;500&display=swap');
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.3; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

/* ── Frame previews — same as ProductPage ── */
const FRAME_META = {
  "Canvas Print": {
    img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80",
    desc: "Arrives rolled in tube packaging. Poly-cotton blend, gloss finish. Scratch, crack and warp resistant. UL Certified ink — water resistant, anti-fade.",
  },
  "Stretched Canvas": {
    img: "https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?w=400&q=80",
    desc: "Ready to hang with image-wrapped sides. Hand-stretched over solid pine bars.",
  },
  "Framed Canvas": {
    img: "https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=400&q=80",
    desc: "Fully mounted and ready to hang. Hand crafted in Kosovo from real pine wood, matte black finish.",
  },
};

/* ════════════════════════════════════════
   LAYOUT
════════════════════════════════════════ */
const Page = styled.div`
  min-height: 100vh;
  background: #080604;
  font-family: "Jost", sans-serif;
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 100vh;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* ── LEFT: upload / preview ── */
const ImageSide = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  background: #0d0b08;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 72px 48px;
  box-sizing: border-box;

  @media (max-width: 900px) {
    position: relative;
    height: auto;
    min-height: 320px;
    padding: 40px 32px;
  }
`;

const DropZone = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed
    ${(p) => (p.$dragover ? "#c9a96e" : "rgba(201,169,110,0.2)")};
  background: ${(p) =>
    p.$dragover ? "rgba(201,169,110,0.04)" : "transparent"};
  cursor: pointer;
  transition:
    border-color 0.25s,
    background 0.25s;
  gap: 16px;
  min-height: 300px;

  &:hover {
    border-color: rgba(201, 169, 110, 0.45);
    background: rgba(201, 169, 110, 0.03);
  }
`;

const DropIcon = styled.div`
  font-size: 2.5rem;
  opacity: 0.25;
`;

const DropText = styled.p`
  font-family: "Jost", sans-serif;
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(245, 237, 224, 0.3);
  margin: 0;
  text-align: center;
`;

const DropSub = styled.p`
  font-size: 0.6rem;
  letter-spacing: 0.14em;
  color: rgba(245, 237, 224, 0.15);
  margin: 0;
  text-align: center;
`;

const PreviewWrap = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const PreviewImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.6),
    0 8px 24px rgba(0, 0, 0, 0.4);
`;

const ChangeImgBtn = styled.button`
  position: absolute;
  bottom: 16px;
  right: 16px;
  background: rgba(8, 6, 4, 0.85);
  border: 1px solid rgba(201, 169, 110, 0.3);
  color: rgba(201, 169, 110, 0.8);
  font-family: "Jost", sans-serif;
  font-size: 0.6rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 8px 16px;
  cursor: pointer;
  backdrop-filter: blur(8px);
  transition:
    border-color 0.2s,
    color 0.2s;

  &:hover {
    border-color: #c9a96e;
    color: #c9a96e;
  }
`;

const UploadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(8, 6, 4, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  backdrop-filter: blur(4px);
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 2px solid rgba(201, 169, 110, 0.2);
  border-top-color: #c9a96e;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const UploadText = styled.p`
  font-size: 0.65rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(201, 169, 110, 0.7);
  margin: 0;
`;

/* ── RIGHT: form ── */
const InfoSide = styled.div`
  padding: 96px 52px 72px;
  overflow-y: auto;
  animation: ${fadeIn} 0.5s ease forwards;

  @media (max-width: 1100px) {
    padding: 96px 36px 60px;
  }
  @media (max-width: 900px) {
    padding: 40px 24px 60px;
  }
`;

const BreadCrumb = styled.p`
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: #c9a96e;
  margin: 0 0 20px 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const PageTitle = styled.h1`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: clamp(2.2rem, 4vw, 3.4rem);
  color: #f5ede0;
  letter-spacing: 0.01em;
  line-height: 1;
  margin: 0 0 6px 0;
  text-transform: uppercase;
`;

const PageSub = styled.p`
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  color: rgba(245, 237, 224, 0.3);
  margin: 0 0 32px 0;
`;

const GoldRule = styled.div`
  height: 1px;
  background: linear-gradient(to right, rgba(201, 169, 110, 0.4), transparent);
  margin-bottom: 32px;
`;

const SectionLabel = styled.p`
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: rgba(245, 237, 224, 0.35);
  margin: 0 0 12px 0;

  span {
    color: #c9a96e;
    margin-left: 8px;
  }
`;

/* ── Name input ── */
const NameInput = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid
    ${(p) => (p.$error ? "rgba(231,76,60,0.5)" : "rgba(201,169,110,0.15)")};
  color: #f5ede0;
  font-family: "Cormorant Garamond", serif;
  font-size: 1.2rem;
  font-weight: 300;
  letter-spacing: 0.04em;
  padding: 12px 16px;
  outline: none;
  box-sizing: border-box;
  margin-bottom: 32px;
  transition: border-color 0.25s;

  &::placeholder {
    color: rgba(245, 237, 224, 0.15);
    font-style: italic;
  }
  &:focus {
    border-color: rgba(201, 169, 110, 0.45);
  }
`;

/* ── Frame cards — identical to ProductPage ── */
const FrameGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 32px;
`;

const FrameCard = styled.button`
  background: ${(p) =>
    p.$active ? "rgba(201,169,110,0.1)" : "rgba(255,255,255,0.02)"};
  border: 1px solid ${(p) => (p.$active ? "#c9a96e" : "rgba(255,255,255,0.07)")};
  cursor: pointer;
  padding: 0;
  overflow: hidden;
  transition:
    border-color 0.25s,
    background 0.25s;

  &:hover {
    border-color: rgba(201, 169, 110, 0.5);
    background: rgba(201, 169, 110, 0.06);
  }
`;

const FrameCardImg = styled.div`
  width: 100%;
  height: 80px;
  overflow: hidden;
  background: #111;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: ${(p) => (p.$active ? 1 : 0.55)};
    transition: opacity 0.25s;
  }
`;

const FrameCardLabel = styled.div`
  padding: 8px 6px 10px;
  font-family: "Jost", sans-serif;
  font-size: 0.58rem;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${(p) => (p.$active ? "#c9a96e" : "rgba(245,237,224,0.4)")};
  transition: color 0.25s;
  line-height: 1.3;
  text-align: center;
`;

/* ── Size pills — identical to ProductPage ── */
const SizePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 36px;
`;

const SizePill = styled.button`
  background: ${(p) => (p.$active ? "rgba(201,169,110,0.12)" : "transparent")};
  border: 1px solid ${(p) => (p.$active ? "#c9a96e" : "rgba(255,255,255,0.1)")};
  color: ${(p) => (p.$active ? "#c9a96e" : "rgba(245,237,224,0.45)")};
  font-family: "Jost", sans-serif;
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  padding: 9px 18px;
  cursor: pointer;
  border-radius: 999px;
  transition: all 0.22s ease;

  &:hover {
    border-color: rgba(201, 169, 110, 0.55);
    color: #c9a96e;
    background: rgba(201, 169, 110, 0.06);
  }
`;

/* ── Price row ── */
const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 32px;
`;

const PriceValue = styled.span`
  font-family: "Cormorant Garamond", serif;
  font-size: 2.4rem;
  font-weight: 300;
  color: #f5ede0;
  letter-spacing: 0.02em;
  transition: all 0.2s ease;
`;

const PriceUnit = styled.span`
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(245, 237, 224, 0.3);
`;

/* ── Upload prompt inside form ── */
const UploadPrompt = styled.div`
  border: 1px dashed rgba(201, 169, 110, 0.2);
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  cursor: pointer;
  transition:
    border-color 0.25s,
    background 0.25s;

  &:hover {
    border-color: rgba(201, 169, 110, 0.45);
    background: rgba(201, 169, 110, 0.03);
  }
`;

const UploadPromptIcon = styled.div`
  font-size: 1.5rem;
  opacity: 0.3;
  flex-shrink: 0;
`;

const UploadPromptText = styled.div``;

const UploadPromptTitle = styled.p`
  font-size: 0.72rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(245, 237, 224, 0.5);
  margin: 0 0 3px 0;
`;

const UploadPromptSub = styled.p`
  font-size: 0.62rem;
  letter-spacing: 0.1em;
  color: rgba(245, 237, 224, 0.2);
  margin: 0;
`;

const UploadedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(46, 204, 113, 0.06);
  border: 1px solid rgba(46, 204, 113, 0.2);
  margin-bottom: 32px;
`;

const UploadedText = styled.span`
  font-size: 0.68rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(46, 204, 113, 0.8);
`;

/* ── Add btn ── */
const AddBtn = styled.button`
  width: 100%;
  background: ${(p) => (p.$disabled ? "rgba(201,169,110,0.15)" : "#c9a96e")};
  color: ${(p) => (p.$disabled ? "rgba(201,169,110,0.35)" : "#080604")};
  border: none;
  padding: 17px 32px;
  font-family: "Jost", sans-serif;
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  transition:
    background 0.25s,
    transform 0.15s;
  margin-bottom: 12px;

  &:hover:not(:disabled) {
    background: #dbb97e;
    transform: translateY(-1px);
  }
`;

const ValidationMsg = styled.p`
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  color: rgba(231, 76, 60, 0.7);
  margin: 0 0 16px 0;
  text-align: center;
`;

const AddedBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.65rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #6fcf97;
  opacity: ${(p) => (p.$visible ? 1 : 0)};
  transition: opacity 0.35s ease;
  height: 24px;
  margin-bottom: 8px;
`;

const DescSection = styled.div`
  margin-top: 40px;
  padding-top: 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const DescCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DescTitle = styled.h4`
  font-family: "Jost", sans-serif;
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #c9a96e;
  margin: 0;
`;

const DescText = styled.p`
  font-size: 0.8rem;
  line-height: 1.75;
  color: rgba(245, 237, 224, 0.4);
  margin: 0;
`;

/* ════════════════════════════════════════
   CLOUDINARY UPLOAD
════════════════════════════════════════ */
const uploadToCloudinary = async (file) => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
  const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", uploadPreset);
  formData.append("folder", "custom-prints");

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
    {
      method: "POST",
      body: formData,
    },
  );
  const data = await res.json();
  if (data.error) throw new Error(data.error.message);

  return {
    imageUrl: data.secure_url, // delivery URL (optimised)
    originalUrl: data.secure_url.replace("/upload/", "/upload/fl_attachment/"), // force download
    publicId: data.public_id,
    width: data.width,
    height: data.height,
    format: data.format,
    bytes: data.bytes,
    originalFilename: data.original_filename,
  };
};

/* ════════════════════════════════════════
   COMPONENT
════════════════════════════════════════ */
const CustomPrint = () => {
  const navigate = useNavigate();
  const { addItem } = useCart();
  const fileInputRef = useRef(null);

  const [pricing, setPricing] = useState(null);
  const [selectedFrame, setSelectedFrame] = useState(FRAME_TYPES[0]);
  const [selectedSize, setSelectedSize] = useState(SIZES[0]);
  const [imageName, setImageName] = useState("");
  const [localPreview, setLocalPreview] = useState(null); // blob URL for preview
  const [uploadedImage, setUploadedImage] = useState(null); // Cloudinary result
  const [uploading, setUploading] = useState(false);
  const [dragover, setDragover] = useState(false);
  const [added, setAdded] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    getPricing().then(setPricing).catch(console.error);
  }, []);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (localPreview) URL.revokeObjectURL(localPreview);
    };
  }, [localPreview]);

  const currentPrice = pricing?.[selectedFrame]?.[selectedSize] ?? null;
  const priceValid = currentPrice && currentPrice > 0;

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith("image/")) return;

    // show local preview immediately
    if (localPreview) URL.revokeObjectURL(localPreview);
    setLocalPreview(URL.createObjectURL(file));
    setUploadedImage(null);
    setUploading(true);

    try {
      const result = await uploadToCloudinary(file);
      setUploadedImage(result);
      // auto-fill name from filename if empty
      if (!imageName) {
        setImageName(result.originalFilename?.replace(/_/g, " ") || "");
      }
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed. Please try again.");
      setLocalPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const onFileChange = (e) => handleFile(e.target.files[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragover(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const validate = () => {
    const e = {};
    if (!uploadedImage) e.image = "Please upload an image.";
    if (!imageName.trim()) e.name = "Please give your print a name.";
    if (!priceValid) e.price = "Price not configured for this combination.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleAddToCart = () => {
    if (!validate()) return;

    addItem({
      productId: `custom_${Date.now()}`, // unique ID for cart dedup
      name: imageName.trim(),
      imageUrl: uploadedImage.imageUrl,
      originalUrl: uploadedImage.originalUrl, // full-res download URL
      publicId: uploadedImage.publicId,
      imageWidth: uploadedImage.width,
      imageHeight: uploadedImage.height,
      imageFormat: uploadedImage.format,
      imageBytes: uploadedImage.bytes,
      category: "Custom Print",
      frameType: selectedFrame,
      size: selectedSize,
      price: currentPrice,
      isCustom: true,
    });

    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  return (
    <>
      <GlobalStyle />
      <Page>
        <Layout>
          {/* ── LEFT: image drop zone / preview ── */}
          <ImageSide>
            {!localPreview ? (
              <DropZone
                $dragover={dragover}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragover(true);
                }}
                onDragLeave={() => setDragover(false)}
                onDrop={onDrop}
              >
                <DropIcon>↑</DropIcon>
                <DropText>Drop your image here</DropText>
                <DropSub>or click to browse · JPG, PNG, TIFF, WEBP</DropSub>
              </DropZone>
            ) : (
              <PreviewWrap>
                <PreviewImg src={localPreview} alt="Your print preview" />

                {uploading && (
                  <UploadingOverlay>
                    <Spinner />
                    <UploadText>Uploading…</UploadText>
                  </UploadingOverlay>
                )}

                {!uploading && (
                  <ChangeImgBtn onClick={() => fileInputRef.current?.click()}>
                    Change image
                  </ChangeImgBtn>
                )}
              </PreviewWrap>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={onFileChange}
            />
          </ImageSide>

          {/* ── RIGHT: form ── */}
          <InfoSide>
            <BreadCrumb onClick={() => navigate(-1)}>← Back</BreadCrumb>

            <PageTitle>Custom Print</PageTitle>
            <PageSub>Upload your image — we handle the rest</PageSub>

            <GoldRule />

            {/* Upload prompt (shown on right side too when no image yet) */}
            {!uploadedImage && !uploading && (
              <UploadPrompt onClick={() => fileInputRef.current?.click()}>
                <UploadPromptIcon>🖼</UploadPromptIcon>
                <UploadPromptText>
                  <UploadPromptTitle>
                    {localPreview
                      ? "Uploading your image…"
                      : "Upload your image"}
                  </UploadPromptTitle>
                  <UploadPromptSub>
                    {localPreview
                      ? "Please wait while we process it"
                      : "Click or drop on the left panel · Max 20MB · JPG, PNG, TIFF"}
                  </UploadPromptSub>
                </UploadPromptText>
              </UploadPrompt>
            )}

            {uploadedImage && (
              <UploadedBadge>
                <span style={{ color: "#2ecc71" }}>✓</span>
                <UploadedText>
                  Image uploaded · {uploadedImage.width}×{uploadedImage.height}
                  px · {(uploadedImage.bytes / 1024 / 1024).toFixed(1)}MB
                </UploadedText>
              </UploadedBadge>
            )}

            {errors.image && <ValidationMsg>{errors.image}</ValidationMsg>}

            {/* Name */}
            <SectionLabel>Print Name</SectionLabel>
            <NameInput
              type="text"
              placeholder="Give your print a name…"
              value={imageName}
              onChange={(e) => {
                setImageName(e.target.value);
                setErrors((p) => ({ ...p, name: null }));
              }}
              $error={!!errors.name}
            />
            {errors.name && (
              <ValidationMsg style={{ marginTop: "-24px" }}>
                {errors.name}
              </ValidationMsg>
            )}

            {/* Frame type */}
            <SectionLabel>
              Material: <span>{selectedFrame}</span>
            </SectionLabel>
            <FrameGrid>
              {FRAME_TYPES.map((frame) => (
                <FrameCard
                  key={frame}
                  $active={selectedFrame === frame}
                  onClick={() => setSelectedFrame(frame)}
                >
                  <FrameCardImg $active={selectedFrame === frame}>
                    <img src={FRAME_META[frame].img} alt={frame} />
                  </FrameCardImg>
                  <FrameCardLabel $active={selectedFrame === frame}>
                    {frame}
                  </FrameCardLabel>
                </FrameCard>
              ))}
            </FrameGrid>

            {/* Size */}
            <SectionLabel>
              Dimensions: <span>{selectedSize}</span>
            </SectionLabel>
            <SizePills>
              {SIZES.map((size) => (
                <SizePill
                  key={size}
                  $active={selectedSize === size}
                  onClick={() => setSelectedSize(size)}
                >
                  {size}
                </SizePill>
              ))}
            </SizePills>

            {/* Price */}
            <PriceRow>
              {priceValid ? (
                <PriceValue>€{currentPrice.toFixed(2)}</PriceValue>
              ) : (
                <PriceValue style={{ color: "rgba(245,237,224,0.2)" }}>
                  —
                </PriceValue>
              )}
              <PriceUnit>
                {selectedFrame} · {selectedSize}
              </PriceUnit>
            </PriceRow>

            <AddedBadge $visible={added}>✓ Added to cart</AddedBadge>

            <AddBtn
              $disabled={!priceValid || uploading}
              disabled={!priceValid || uploading}
              onClick={handleAddToCart}
            >
              {uploading
                ? "Uploading image…"
                : priceValid
                  ? "Add to Cart"
                  : "Unavailable"}
            </AddBtn>

            {errors.price && <ValidationMsg>{errors.price}</ValidationMsg>}

            {/* Frame descriptions */}
            <DescSection>
              {FRAME_TYPES.map((frame) => (
                <DescCard key={frame}>
                  <DescTitle>{frame}</DescTitle>
                  <DescText>{FRAME_META[frame].desc}</DescText>
                </DescCard>
              ))}
            </DescSection>
          </InfoSide>
        </Layout>
      </Page>
    </>
  );
};

export default CustomPrint;
